/**
 * 需求来源：Plane DEV-126262《【CJ主站】cj-business-travel 页面模块添加需求》；
 *          视觉/交互基准 docs/旅游落地页/商旅的视频.html（提出方直接给的现成模块源码，与工单描述内嵌那份同源）。
 *          DEV-129113（2026-09-15）追加「自动滚动播放（保留手动点击）」+ 两条新视频，
 *          基准换成新版模块源码 test/fixtures/cityVideos260915.html（下称「新版源码」）。
 * 注意事项：
 * 1. 位置由开发当场拍板：紧跟 featured-china-experiences（区块 4）之后；背景与内容区宽度不用源码自带的
 *    那套，改为与区块 4 完全一致（样式在 index.less 里与 .cjbt-xp-luxe 取同一份声明，单测 CASE-V6 锁住）。
 * 2. 类名由源码的 cj-city-video-* 改前缀为 cjbt-city-video-*：cj- 是主站全局命名空间（如 .cj-container），
 *    整页其它八个区块也一律用 cjbt- 前缀，沿用源码前缀会有跨页样式串味的风险。
 * 3. 封面图不用源码里的 https://img.youtube.com/vi/<id>/hqdefault.jpg 外链 —— 按需求「下载到本地、
 *    上传 OSS 后再使用」改走本页统一的公共资源仓取址（序号顺延 77~83，见 images.js）；
 *    **播放地址仍直连 YouTube**（需求：视频链接直接用 YouTube 的）。
 * 4. iframe 点击后才创建（源码是 media.innerHTML=…，此处改为 React 受控渲染，不用 dangerouslySetInnerHTML）：
 *    首屏若直接挂 7 个 YouTube 播放器，等于给每个访客白加载 7 份第三方脚本。SSR 产出必须无 iframe，
 *    单测 CASE-V4 对此有断言。
 * 5. 卡片保留源码的 role="button" + tabIndex=0 + Enter/Space 键盘触发，键盘可达性与源码一致。
 * 6. **轮播（DEV-129113）**：新版源码的内联脚本是命令式 DOM 操作（querySelector + innerHTML +
 *    手工建 dot 节点），此处改为 React 受控实现，行为逐条对齐（节奏 4500ms、到尾回环、悬停暂停、
 *    手动操作后重新计时、滑动阈值 45px、resize 防抖 120ms、点开视频后自动滚动永久停）。
 *    位移量沿用源码做法**实测**首张卡宽 + 计算样式里的 gap，不在 JS 里重算一遍 CSS —— 卡宽与 gap
 *    在三端各不相同（见 index.less），JS 里再写一份必然与 CSS 分叉。
 * 7. **每屏卡片数改按站内三端断点**：源码是 600/900/1180 三个原生断点，本页既定方案是
 *    .mobile()(≤700)/.tablet()(701~1200)/.desktop()(>1200)（CASE-A6/CASE-V7 锁住不许出现原生断点）。
 *    取值让「一屏几张」与已上线版本的「一行几张」保持一致：桌面 5、平板 3、手机 1，
 *    即工单「平板与手机适配沿用已上线版本」在轮播形态下的等价落法（换行 → 横向滚动，张数不变）。
 *    这份取值与 index.less 的 flex-basis 是同一口径，改一边必须改另一边（CASE-V8 跨文件锁住）。
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';

var EMBED_BASE = 'https://www.youtube.com/embed/';
// 播放参数逐字取自模块源码内联脚本，改动会与需求给的行为不一致（单测 CASE-V3 逐字比对）
var EMBED_QUERY = '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

// 轮播参数逐字取自新版源码内联脚本（CASE-V9 逐条比对，不许在这里"顺手调一下手感"）
var AUTOPLAY_DELAY = 4500;
var SWIPE_THRESHOLD = 45;
var RESIZE_DEBOUNCE = 120;

// 每屏卡片数（见文件头注 7）。BREAKPOINT_* 与 responsive-utils.less 的 @breakpoint-sm/@breakpoint-md 同值。
var ITEMS_PER_VIEW_DESKTOP = 5;
var ITEMS_PER_VIEW_TABLET = 3;
var ITEMS_PER_VIEW_MOBILE = 1;
var BREAKPOINT_MOBILE = 700;
var BREAKPOINT_TABLET = 1200;

/**
 * 拼 YouTube 就地内嵌播放地址。
 * @param {string} videoId YouTube 视频 ID
 * @returns {string} 播放地址；videoId 为空/非字符串时返回空串（不拼出半截指向 YouTube 首页的地址）
 */
export function buildEmbedUrl(videoId) {
  if (!videoId || typeof videoId !== 'string') return '';
  return EMBED_BASE + encodeURIComponent(videoId) + EMBED_QUERY;
}

/**
 * 视口宽 → 每屏卡片数。
 * @param {number} width 视口宽度（px）
 * @returns {number} 每屏卡片数；width 取不到（SSR、0、NaN）时按桌面档，
 *   保证服务端与客户端首次渲染的 DOM 完全一致、不触发 hydrate 失配。
 */
export function getItemsPerView(width) {
  if (!(width > 0)) return ITEMS_PER_VIEW_DESKTOP;
  if (width <= BREAKPOINT_MOBILE) return ITEMS_PER_VIEW_MOBILE;
  if (width <= BREAKPOINT_TABLET) return ITEMS_PER_VIEW_TABLET;
  return ITEMS_PER_VIEW_DESKTOP;
}

/**
 * 每屏卡片数 —— **与 CSS 同源**的取法，组件实际用的是这个。
 *
 * 为什么不直接用 window.innerWidth（那是 getItemsPerView 的入参）：
 * `window.innerWidth` **包含经典滚动条宽度**，而 CSS 媒体查询用的是不含滚动条的布局视口宽。
 * 在 Windows / 开了「总是显示滚动条」的 macOS 上两者差约 15px，于是 1201~1215 这一带
 * CSS 已经切到 .tablet()（一屏 3 张）、JS 还认为是桌面档（5 张）：maxIndex 被算成 2 而不是 4，
 * **最后两张卡永远划不到**，而且 next 按钮会提前变 disabled（看起来就是「箭头莫名其妙消失」）。
 * matchMedia 与媒体查询是同一个求值引擎，不会分叉。
 *
 * @param {Object} [win] window 对象（注入便于测试）
 * @returns {number} 每屏卡片数；拿不到 matchMedia 时退回按 innerWidth 判断
 */
export function matchItemsPerView(win) {
  var w = win || (typeof window === 'undefined' ? null : window);
  if (!w) return ITEMS_PER_VIEW_DESKTOP;
  if (!w.matchMedia) return getItemsPerView(w.innerWidth);
  if (w.matchMedia('(max-width: ' + BREAKPOINT_MOBILE + 'px)').matches) return ITEMS_PER_VIEW_MOBILE;
  if (w.matchMedia('(max-width: ' + BREAKPOINT_TABLET + 'px)').matches) return ITEMS_PER_VIEW_TABLET;
  return ITEMS_PER_VIEW_DESKTOP;
}

/**
 * 可滚动到的最大页码（源码 getMaxIndex）。卡片数不足一屏时为 0，即整体不滚动。
 * @param {number} total 卡片总数
 * @param {number} perView 每屏卡片数
 * @returns {number} 最大页码
 */
export function getMaxIndex(total, perView) {
  return Math.max(0, (total || 0) - (perView || 1));
}

/**
 * 下一页页码（源码 nextSlide）：到尾回环到 0。
 * @param {number} current 当前页码
 * @param {number} maxIndex 最大页码
 * @returns {number} 目标页码
 */
export function nextIndex(current, maxIndex) {
  if (maxIndex <= 0) return 0;
  return current >= maxIndex ? 0 : current + 1;
}

/**
 * 上一页页码（源码 previousSlide）：到头回环到尾。
 * @param {number} current 当前页码
 * @param {number} maxIndex 最大页码
 * @returns {number} 目标页码
 */
export function prevIndex(current, maxIndex) {
  if (maxIndex <= 0) return 0;
  return current <= 0 ? maxIndex : current - 1;
}

/**
 * 把页码夹回界内（源码 updateCarousel 开头的两段 clamp）。
 * 视口变宽时 maxIndex 会收缩，不夹就会把整条轨道推出可视区、留一片空白。
 * @param {number} index 当前页码
 * @param {number} maxIndex 最大页码
 * @returns {number} 夹紧后的页码
 */
export function clampIndex(index, maxIndex) {
  return Math.min(Math.max(index || 0, 0), Math.max(maxIndex, 0));
}

/**
 * 此刻该不该自动滚动。四个闸门任一命中即停，对应源码 startAutoplay 的 isVideoPlaying 守卫、
 * mouseenter 的 stopAutoplay、以及 maxIndex<=0 时 nextSlide 直接 return。
 * @param {Object} state
 * @param {boolean} state.hasPlayed 是否已点开过任一视频（点开后**永久**停，源码同）
 * @param {boolean} state.paused 鼠标悬停 / 触摸拖动期间
 * @param {boolean} state.reduceMotion 用户要求减少动效
 * @param {number} state.maxIndex 最大页码；0 表示卡片不足一屏、整体不滚
 * @returns {boolean} true 表示应当挂上定时器
 */
export function shouldAutoplay(state) {
  var s = state || {};
  return !s.hasPlayed && !s.paused && !s.reduceMotion && s.maxIndex > 0;
}

/**
 * 一次滑动该翻向哪边（源码 touchend 里的阈值判断）。
 * @param {number} deltaX 结束点 X − 起始点 X；负数为左滑
 * @returns {string|null} 'next' / 'prev'；未超过阈值返回 null（不翻页）
 */
export function swipeIntent(deltaX) {
  if (!(Math.abs(deltaX) > SWIPE_THRESHOLD)) return null;
  return deltaX < 0 ? 'next' : 'prev';
}

/**
 * 该按键是否触发播放（源码 keydown 只认 Enter 与空格）。
 * @param {string} key KeyboardEvent.key
 * @returns {boolean}
 */
export function isActivationKey(key) {
  return key === 'Enter' || key === ' ';
}

/**
 * 轮播参数的运行时快照，供单测直接比对（不必去正则扫源码 —— 扫源码扫的是"我自己写了什么"，
 * 比对运行时常量才是"跑起来到底用了什么"）。冻结以防被测试或调用方改掉。
 */
export var CAROUSEL_PARAMS = Object.freeze({
  autoplayDelay: AUTOPLAY_DELAY,
  swipeThreshold: SWIPE_THRESHOLD,
  resizeDebounce: RESIZE_DEBOUNCE,
  itemsPerView: Object.freeze({
    desktop: ITEMS_PER_VIEW_DESKTOP,
    tablet: ITEMS_PER_VIEW_TABLET,
    mobile: ITEMS_PER_VIEW_MOBILE,
  }),
  breakpoints: Object.freeze({
    mobile: BREAKPOINT_MOBILE,
    tablet: BREAKPOINT_TABLET,
  }),
});

/** 取轨道上实际生效的列间距（px）。CSS 是 gap 的唯一事实来源，这里只读不算。 */
function readGap(track) {
  if (!track || typeof window === 'undefined' || !window.getComputedStyle) return 0;
  var styles = window.getComputedStyle(track);
  return parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
}

/** 用户是否要求减少动效；true 时不自动滚动（本模块 index.less 已有 prefers-reduced-motion 块，此处与之同口径）。 */
function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function CityVideos(props) {
  var data = props.data || {};
  var items = data.items || [];
  var a11y = data.a11y || {};
  // 源码里每张卡各自独立加载、互不影响（点了 A 再点 B，两个都在播），故用「已播放集合」而非单值
  var [played, setPlayed] = useState({});
  var [index, setIndex] = useState(0);
  // SSR 与客户端首帧都用桌面档，保证两边 DOM 完全一致、不触发 hydrate 失配；
  // 挂载后的 effect 里才用 matchItemsPerView 读真实档位。
  var [perView, setPerView] = useState(ITEMS_PER_VIEW_DESKTOP);
  // 同档位内拉宽窗口卡宽也会变，位移量要跟着重算；perView 不变时靠这个计数触发重测。
  var [measureTick, setMeasureTick] = useState(0);
  // 鼠标悬停 / 触摸拖动期间暂停，对应源码的 stopAutoplay + mouseleave 重启
  var [paused, setPaused] = useState(false);
  // 手动翻页后重新计时（源码 restartAutoplay）：自增即可让下面的 interval effect 重建
  var [autoplayEpoch, setAutoplayEpoch] = useState(0);
  var [reduceMotion, setReduceMotion] = useState(false);

  var trackRef = useRef(null);
  var firstItemRef = useRef(null);
  var touchStartX = useRef(0);

  var maxIndex = getMaxIndex(items.length, perView);
  // 点开过任意一个视频后自动滚动**永久**停（源码 isVideoPlaying 置位后不再 restart），
  // 但手动点箭头/页码点仍可翻页 —— 工单明确「自动滚动与手动点击都要有，不是二选一」。
  var hasPlayed = Object.keys(played).length > 0;

  var play = useCallback(function (id) {
    setPlayed(function (prev) {
      if (prev[id]) return prev;
      var next = {};
      Object.keys(prev).forEach(function (k) { next[k] = prev[k]; });
      next[id] = true;
      return next;
    });
  }, []);

  var handleKeyDown = useCallback(function (event, id) {
    if (!isActivationKey(event.key)) return;
    event.preventDefault();
    play(id);
  }, [play]);

  var goTo = useCallback(function (target) {
    setIndex(target);
    setAutoplayEpoch(function (n) { return n + 1; });
  }, []);

  var goNext = useCallback(function () {
    setIndex(function (cur) { return nextIndex(cur, maxIndex); });
    setAutoplayEpoch(function (n) { return n + 1; });
  }, [maxIndex]);

  var goPrev = useCallback(function () {
    setIndex(function (cur) { return prevIndex(cur, maxIndex); });
    setAutoplayEpoch(function (n) { return n + 1; });
  }, [maxIndex]);

  // 量视口：挂载后读一次 + resize 防抖重读（源码 resizeTimer 120ms）
  useEffect(function () {
    function read() {
      setPerView(matchItemsPerView(window));
      setMeasureTick(function (t) { return t + 1; });
    }
    read();
    setReduceMotion(prefersReducedMotion());
    var timer = null;
    function onResize() {
      clearTimeout(timer);
      timer = setTimeout(read, RESIZE_DEBOUNCE);
    }
    window.addEventListener('resize', onResize);
    return function () {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // 视口变窄/变宽导致 maxIndex 收缩时把页码拉回界内（源码 updateCarousel 的 clamp）
  useEffect(function () {
    setIndex(function (cur) { return clampIndex(cur, maxIndex); });
  }, [maxIndex]);

  // 位移：实测首张卡宽 + 计算样式里的 gap，不在 JS 里重算 CSS（见文件头注 6）。
  // measureTick 进依赖是因为同一档位内拉宽窗口卡宽也会变，只盯 perView 会漏掉这一半情况。
  useEffect(function () {
    var track = trackRef.current;
    var first = firstItemRef.current;
    if (!track || !first) return;
    var step = first.getBoundingClientRect().width + readGap(track);
    track.style.transform = 'translate3d(' + (-index * step) + 'px, 0, 0)';
  }, [index, perView, measureTick, items.length]);

  // 自动滚动。开不开由 shouldAutoplay 这一个纯函数判定（四个闸门都在它里面，单测直接打它）；
  // autoplayEpoch 进依赖是为了「手动翻页后重新计时」——它一变，本 effect 重建、定时器归零。
  useEffect(function () {
    if (!shouldAutoplay({ hasPlayed: hasPlayed, paused: paused, reduceMotion: reduceMotion, maxIndex: maxIndex })) {
      return undefined;
    }
    var timer = setInterval(function () {
      setIndex(function (cur) { return nextIndex(cur, maxIndex); });
    }, AUTOPLAY_DELAY);
    return function () { clearInterval(timer); };
  }, [hasPlayed, paused, reduceMotion, maxIndex, autoplayEpoch]);

  var onTouchStart = useCallback(function (event) {
    touchStartX.current = event.touches[0].clientX;
    setPaused(true);
  }, []);

  var onTouchEnd = useCallback(function (event) {
    var intent = swipeIntent(event.changedTouches[0].clientX - touchStartX.current);
    if (intent === 'next') goNext();
    else if (intent === 'prev') goPrev();
    setPaused(false);
  }, [goNext, goPrev]);

  var dots = [];
  for (var d = 0; d <= maxIndex; d += 1) dots.push(d);

  return (
    <section className="cjbt-city-video" id="china-city-videos">
      <div className="cjbt-city-video-wrap">
        <div
          className="cjbt-city-video-carousel"
          onMouseEnter={function () { setPaused(true); }}
          onMouseLeave={function () { setPaused(false); }}
        >
          <button
            className="cjbt-city-video-arrow cjbt-city-video-prev"
            type="button"
            aria-label={a11y.prev}
            disabled={index === 0}
            onClick={goPrev}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 18l-6-6 6-6"></path>
            </svg>
          </button>

          <div
            className="cjbt-city-video-viewport"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="cjbt-city-video-list" ref={trackRef}>
              {items.map(function (item, i) {
                var isPlaying = !!played[item.id];
                return (
                  <div
                    className="cjbt-city-video-item"
                    key={item.id}
                    ref={i === 0 ? firstItemRef : null}
                  >
                    <article className="cjbt-city-video-card">
                      <div
                        className={'cjbt-city-video-media' + (isPlaying ? ' is-playing' : '')}
                        data-video={item.videoId}
                        role="button"
                        tabIndex={0}
                        aria-label={item.ariaLabel}
                        onClick={function () { play(item.id); }}
                        onKeyDown={function (event) { handleKeyDown(event, item.id); }}
                      >
                        {isPlaying ? (
                          <iframe
                            src={buildEmbedUrl(item.videoId)}
                            title={item.name}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        ) : (
                          <React.Fragment>
                            <img src={item.image} alt={item.alt} loading="lazy" />

                            <div className="cjbt-city-video-overlay">
                              <span className="cjbt-city-video-play"></span>
                            </div>

                            <div className="cjbt-city-video-index">{item.number}</div>

                            <div className="cjbt-city-video-info">
                              <span className="cjbt-city-video-location">{item.location}</span>
                              <h3 className="cjbt-city-video-name">{item.name}</h3>
                            </div>

                            <span className="cjbt-city-video-dot"></span>
                          </React.Fragment>
                        )}
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="cjbt-city-video-arrow cjbt-city-video-next"
            type="button"
            aria-label={a11y.next}
            disabled={index >= maxIndex}
            onClick={goNext}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 18l6-6-6-6"></path>
            </svg>
          </button>

          <div className="cjbt-city-video-pagination">
            {dots.map(function (dot) {
              return (
                <button
                  className={'cjbt-city-video-page-dot' + (dot === index ? ' active' : '')}
                  type="button"
                  key={dot}
                  aria-label={a11y.goto ? a11y.goto + ' ' + (dot + 1) : undefined}
                  onClick={function () { goTo(dot); }}
                ></button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
